import { useCallback } from "react";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type {
  EditorView,
  PlannerPlaybook,
  PlannerStage,
} from "../pages/workspace/planner/types.js";

/**
 * In-flight Planner state.
 *
 * Persisted across navigation so that if a user leaves the Planner mid-flow
 * (e.g. to ask XAMI a question, or to peek at the inbox) they can return
 * without losing the path they chose, the stage they were on, or the draft
 * stepGraph the backend already produced for them.
 *
 * Keyed by roadmapEntryId so two different entries don't trample each other.
 * When a user kicks off a fresh planning session for the same entry, call
 * `reset(entryId)` to clear stale state.
 */
/** One measurable success metric — mirrors server SuccessMetric shape. */
export interface PlannerSuccessMetric {
  label: string;
  target: string;
  baseline?: string;
  measurement_window?: string;
  horizon_weeks?: number;
}

export interface PlannerSession {
  stage: PlannerStage;
  editorView: EditorView;
  showDeps: boolean;
  selectedStepId: string | null;
  /** Backend-supplied draft once decompose / discovery completes. */
  draftPlay: PlannerPlaybook | null;
  /** PlanIt response used to launch the play (templateId etc.). */
  decompositionInputId: string | null;
  /** ID of the in-progress Discovery session, if any. */
  discoverySessionId: string | null;
  /** Override template chosen on the Template stage. */
  overrideTemplateId: string | null;
  /**
   * Captured outcome (Discovery Phase 1 / Template-or-Quick form).
   * Carried in the store so the Editor / Review headers can show it
   * before the play row exists.
   */
  outcomeStatement: string | null;
  /** Captured success metrics (Discovery Phase 6 or equivalent). */
  successMetrics: PlannerSuccessMetric[];
}

const DEFAULT_SESSION: PlannerSession = {
  stage: "chooser",
  editorView: "list",
  showDeps: true,
  selectedStepId: null,
  draftPlay: null,
  decompositionInputId: null,
  discoverySessionId: null,
  overrideTemplateId: null,
  outcomeStatement: null,
  successMetrics: [],
};

export interface PlannerStoreState {
  /** Map of roadmap entry id (or "__default__") → in-flight session. */
  sessions: Record<string, PlannerSession>;
  isLoadingGlobal: boolean;
  get(entryId: string): PlannerSession;
  patch(entryId: string, patch: Partial<PlannerSession>): void;
  reset(entryId: string): void;
  setLoadingGlobal(isLoading: boolean): void;
}

const KEY = "__default__";

export const usePlannerStore = create<PlannerStoreState>()(
  persist(
    (set, getState) => ({
      sessions: {},
      isLoadingGlobal: false,
      get(entryId: string) {
        const k = entryId || KEY;
        return getState().sessions[k] ?? DEFAULT_SESSION;
      },
      patch(entryId, patch) {
        const k = entryId || KEY;
        set((s) => ({
          sessions: {
            ...s.sessions,
            [k]: { ...(s.sessions[k] ?? DEFAULT_SESSION), ...patch },
          },
        }));
      },
      reset(entryId) {
        const k = entryId || KEY;
        set((s) => {
          const next = { ...s.sessions };
          delete next[k];
          return { sessions: next };
        });
      },
      setLoadingGlobal(isLoading: boolean) {
        set({ isLoadingGlobal: isLoading });
      },
    }),
    {
      name: "playstudio.planner",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ sessions: s.sessions }),
    },
  ),
);

/**
 * Convenience hook: read + patch a single entry's session by id without
 * having to chain `get` and `patch` in callers. Subscribes the caller
 * narrowly (re-renders only when that entry's state changes).
 */
export function usePlannerSession(entryId: string): {
  session: PlannerSession;
  patch: (p: Partial<PlannerSession>) => void;
  reset: () => void;
} {
  const k = entryId || KEY;
  const session = usePlannerStore(
    (s) => s.sessions[k] ?? DEFAULT_SESSION,
  );
  const patchAll = usePlannerStore((s) => s.patch);
  const resetAll = usePlannerStore((s) => s.reset);
  const patch = useCallback((p: Partial<PlannerSession>) => patchAll(k, p), [patchAll, k]);
  const reset = useCallback(() => resetAll(k), [resetAll, k]);
  return {
    session,
    patch,
    reset,
  };
}

export const setGlobalLoading = (isLoading: boolean) => {
  usePlannerStore.getState().setLoadingGlobal(isLoading);
};
